import { Box, Collapse, Flex, Text, Tooltip, VStack } from '@chakra-ui/react'
import { BasicPregnancyPreview } from '@components/Assessments/AssessmentPreview'
import { InnerForm } from '@components/forms/FinalForm/InnerForm'
import { ThemeContext, useApp } from '@hb/react'
import {
  CheckboxField,
  colors,
  CreateNewAssessmentArgs,
  DateField,
  DropdownField,
  FieldMap,
  FieldTypes,
  getAssessmentName,
  getMostRecentAssessment,
  notesField,
  TextField,
  UpdateCallback,
} from '@hb/shared'
import { useMemo } from 'react'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { FormElement, SimpleFormFooter } from '../../../forms'
import { DefaultModal } from '../../../Modals/DefaultModal'

const titleField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Pregnancy name',
  optional: true,
}

const getCopyPreviousDataField = (appName: string): CheckboxField => {
  if (appName === 'app') {
    return {
      type: FieldTypes.CHECKBOX,
      placeholder: 'Copy previous data (Pregnancy and sign-on)',
    }
  }
  return {
    type: FieldTypes.CHECKBOX,
    placeholder: 'Copy previous pregnancy data (update as needed)',
  }
}

const sendInviteField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Email patient invite with link to Hamilton Billing',
}

const newPregnancyEddField: DateField = {
  type: FieldTypes.DATE,
  minDate: 'now',
  placeholder: 'Expected Date of Delivery',
}

export const AddAssessmentAlert = ({
  isOpen,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean
  onConfirm: (data: CreateNewAssessmentArgs) => Promise<UpdateCallback>
  onCancel: () => void
}) => {
  const { appName } = useApp()
  const { assessments, user, assessmentId } = useProfile()
  const { email } = user ?? {}
  const mostRecentAssessment = useMemo(
    () => getMostRecentAssessment(assessments ?? {}),
    [assessments],
  )
  const defaultValue: CreateNewAssessmentArgs = useMemo(
    () => ({
      copyPreviousData: false,
      sendInvite: false,
      copyFromPregnancyId: assessmentId ?? undefined,
      name: '',
      edd: undefined,
      notes: '',
    }),
    [assessmentId],
  )

  const copyPreviousDataField = useMemo(() => getCopyPreviousDataField(appName), [appName])

  const copyFromPregnancyField = useMemo<DropdownField>(() => {
    const options = Object.entries(assessments ?? {})
      .map(([id, assessment]) => ({
        id,
        text: getAssessmentName(assessment),
      }))
      .sort((a, b) => {
        const aCreatedOn = assessments?.[a.id].createdOn ?? 0
        const bCreatedOn = assessments?.[b.id].createdOn ?? 0
        return bCreatedOn - aCreatedOn
      })
    return {
      type: FieldTypes.DROPDOWN,
      placeholder: 'Select pregnancy to copy from',
      condition: data => !!data?.copyPreviousData,
      renderOption: option => {
        const assessment = assessments?.[option.id]
        return assessment ? (
          <BasicPregnancyPreview
            shortName
            isThisPregnancy={option.id === assessmentId}
            flexProps={{ boxShadow: 'none', bg: 'transparent' }}
            pregnancy={assessment}
          />
        ) : null
      },
      options,
    }
  }, [assessments, assessmentId])

  const field = useMemo<FieldMap>(
    () => ({
      name: 'New Pregnancy',
      children: {
        name: titleField,
        edd: newPregnancyEddField,
        sendInvite: sendInviteField,
        copyPreviousData: copyPreviousDataField,
        copyFromPregnancyId: copyFromPregnancyField,
        notes: notesField,
      },
    }),
    [copyPreviousDataField, copyFromPregnancyField],
  )

  // const { item: mostRecentMidwife } = useCollectionItem(
  //   midwivesCollection,
  //   mostRecentAssessment?.midwifeId ?? undefined,
  // )

  return (
    <DefaultModal
      isOpen={isOpen}
      size="md"
      overlayHeader
      closeOnOverlayClick={false}
      onClose={onCancel}
      render={() => (
        <VStack w="100%" spacing={0} align="flex-start">
          <Text
            fontFamily="Comfortaa"
            // fontSize='lg'
            color={colors.green.hex}
            px={4}
            fontWeight={500}
            pt={4}>
            NEW PREGNANCY
          </Text>
          <ThemeContext.Provider value={{ theme: 'detailed' }}>
            <InnerForm<CreateNewAssessmentArgs>
              data={defaultValue}
              field={field}
              setUploads={() => {}}
              onSubmit={data => onConfirm(data)}>
              {({ values }) => (
                <VStack w="100%" px={4}>
                  <VStack align="flex-start" w="100%" spacing={1}>
                    {appName === 'app' ? <FormElement name="name" field={titleField} /> : null}
                    <FormElement name="edd" field={newPregnancyEddField} />
                    <Tooltip
                      bg="orange.600"
                      label={!email ? 'Patient email is required to send an invite' : ''}>
                      <Box>
                        <FormElement disabled={!email} name="sendInvite" field={sendInviteField} />
                      </Box>
                    </Tooltip>
                    <Tooltip
                      bg="orange.600"
                      label={
                        !mostRecentAssessment ? 'No previous assessments to copy data from' : ''
                      }>
                      <Box>
                        <FormElement
                          disabled={!mostRecentAssessment}
                          name="copyPreviousData"
                          field={copyPreviousDataField}
                        />
                        <FormElement name="copyFromPregnancyId" field={copyFromPregnancyField} />
                      </Box>
                    </Tooltip>
                    <FormElement name="notes" field={notesField} />
                    <Collapse
                      in={values?.copyPreviousData}
                      style={{
                        display: 'flex',
                        flexFlow: 'column',
                        width: '100%',
                      }}>
                      <Flex gap={1} w="100%" px={2} flexFlow="column">
                        <Text fontFamily="Hero-New" fontSize="sm">
                          The following will be copied from the previous{' '}
                          {appName === 'app' ? 'assessment' : 'pregnancy'}:
                        </Text>
                        <Flex flexFlow="column">
                          {/* {mostRecentMidwife && appName === 'app' ? ( */}
                          <Text
                            fontWeight={500}
                            color="gray.600"
                            fontSize="sm"
                            fontFamily="Open Sans">
                            • Insurance Info
                          </Text>
                          <Text
                            fontWeight={500}
                            color="gray.600"
                            fontSize="sm"
                            fontFamily="Open Sans">
                            {appName === 'app'
                              ? '• Sign On Info (excluding birth-specific info)'
                              : '• Address, Phone, Partner / Policy Info'}
                          </Text>
                        </Flex>
                        <Text fontFamily="Hero-New" fontSize="sm">
                          Please reverify benefits and sign on info
                        </Text>
                      </Flex>
                    </Collapse>
                  </VStack>
                  <SimpleFormFooter hasResetButton submitText="Create Pregnancy" />
                </VStack>
              )}
            </InnerForm>
          </ThemeContext.Provider>
        </VStack>
      )}
    />
  )
}
